import classNames from "classnames"
import { SignalSlashIcon } from "@heroicons/react/24/solid"
import { useAppSelector } from "@/store/hooks"
import { allPieces } from "@/utils/data"
import { gameRoles } from "@/utils/gameRoles"
import Money from "@/Components/Money"
import { getPlayerAvatar } from "@/utils/playerAvatar"

export interface PlayerCardsPlayerItemProps {
  player: IPlayer
  user: IUser
  selected: boolean
  onClick: () => void
  // Set by the two tax cards, which charge a flat rate per property/house — the
  // row then spells out what this particular target would actually pay.
  tax?: { unit: "property" | "house"; rate: number }
}

// The target picker for player-targeting action cards. Deliberately separate
// from PlayerListItemInfo: this one is a roomy, standalone row that spells out
// how many properties and houses the target holds, which is what the choice
// actually turns on. The board's list item is compact by necessity and carries
// board-only behaviour (money fly animations, registering itself as a global
// element for those animations to aim at) that has no place in a modal.
const PlayerCardsPlayerItem: React.FC<PlayerCardsPlayerItemProps> = ({
  player,
  user,
  selected,
  onClick,
  tax,
}) => {
  const housesData = useAppSelector(
    (state) => state.global.myGame?.situation?.housesData
  )
  const piece = allPieces.find((item) => item.id === player.pieceId)
  const role = gameRoles.find((r) => r.id === player.role)
  const avatar = getPlayerAvatar(player, user)

  const propertyCount = player.properties.length
  const houseCount = (housesData || []).reduce(
    (total, houseData) =>
      player.properties.includes(houseData.propertyOrder)
        ? total + houseData.houses
        : total,
    0
  )
  const taxableCount = tax?.unit === "house" ? houseCount : propertyCount
  const taxTotal = tax ? taxableCount * tax.rate : 0

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`game.player-cards-target-${player.order}`}
      className={classNames(
        "flex items-center gap-3 w-full min-h-[5.5rem] px-3 py-3 rounded-lg border text-left transition-all duration-150",
        selected
          ? "bg-green-500/10 border-green-500 ring-2 ring-green-500"
          : "bg-black/40 border-white/10 hover:border-white/30 hover:bg-white/5"
      )}
    >
      <div className="relative shrink-0">
        <img
          className="h-14 w-14 rounded-full object-cover"
          src={user.profileImage ? encodeURI(user.profileImage) : avatar}
        ></img>
        {piece?.imgSrc && (
          <img
            className="absolute -bottom-1 -right-1 h-7 w-7 drop-shadow-[0_1px_2px_black]"
            src={piece.imgSrc}
          ></img>
        )}
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          {player.disconnected && (
            <SignalSlashIcon color="white" className="h-5 w-5 shrink-0" />
          )}
          <span className="font-poppins text-white text-lg font-semibold truncate">
            {user.username}
          </span>
          {role && player.role !== "none" && (
            <span
              className="px-2 py-0.5 rounded-md text-xs font-medium font-poppins whitespace-nowrap shrink-0"
              style={{
                backgroundColor: role.color + "40",
                color: role.color,
              }}
            >
              {role.title}
            </span>
          )}
        </div>

        {/* Under a tax card the other count is dead weight — house tax doesn't
            care how many properties are held, and vice versa — so only the
            count being charged survives, as the basis for the figure on the
            right. The rate itself is stated once above the list. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/60 text-sm font-poppins">
          {(!tax || tax.unit === "property") && (
            <span
              className="flex items-center gap-1.5 whitespace-nowrap"
              data-testid={`game.player-property-count-${player.order}`}
            >
              <i className="fa-solid fa-building text-xs text-white/40"></i>
              {propertyCount} {propertyCount === 1 ? "Property" : "Properties"}
            </span>
          )}
          {(!tax || tax.unit === "house") && (
            <span
              className="flex items-center gap-1.5 whitespace-nowrap"
              data-testid={`game.player-house-count-${player.order}`}
            >
              <i className="fa-solid fa-house text-xs text-white/40"></i>
              {houseCount} {houseCount === 1 ? "House" : "Houses"}
            </span>
          )}
        </div>
      </div>

      {/* The balance stays the row's headline number, at the same size it has
          without a tax card. What the tax takes hangs off it as a smaller
          amber deduction — related to the balance, plainly subordinate to it,
          and needing no "will pay" label to say so. No testId on the balance:
          the board's player list is still mounted behind the modal and already
          owns `game.player-money-${player.order}`. */}
      <div className="shrink-0 flex flex-col items-end gap-1">
        <Money
          money={player.money}
          iconClass="h-1/2"
          className="text-white text-2xl font-semibold font-poppins gap-1 leading-none"
        ></Money>
        {tax && (
          <div className="flex items-center gap-1.5 leading-none">
            <span className="font-poppins text-xs text-white/40 whitespace-nowrap">
              Will pay
            </span>
            <Money
              money={taxTotal}
              className={classNames(
                "text-base font-semibold font-poppins gap-0.5 leading-none",
                taxTotal > 0 ? "text-amber-300" : "text-white/30"
              )}
              testId={`game.player-cards-tax-total-${player.order}`}
            />
          </div>
        )}
      </div>
    </button>
  )
}

export default PlayerCardsPlayerItem
